import React from "react";
import { AnimationHandler } from "../../../dashboard/carousel/components";
import { getAdvanceBy } from "../../helpers/templateUtils";

/**
 * Grid positions for the 14 border items in the grid-5x4-center template.
 * Index 0 is null because the center item occupies that slot.
 */
const BORDER_POSITIONS = [
  null,
  { col: "1", row: "1" },
  { col: "2", row: "1" },
  { col: "3", row: "1" },
  { col: "4", row: "1" },
  { col: "5", row: "1" },
  { col: "1", row: "2" },
  { col: "5", row: "2" },
  { col: "1", row: "3" },
  { col: "5", row: "3" },
  { col: "1", row: "4" },
  { col: "2", row: "4" },
  { col: "3", row: "4" },
  { col: "4", row: "4" },
  { col: "5", row: "4" },
];

/**
 * Warhol tint names applied to the four quadrants of a 4up-warhol template.
 */
const WARHOL_TINTS = [
  "warhol-yellow",
  "warhol-cyan",
  "warhol-magenta",
  "warhol-green",
];

/**
 * Build the array of slide items for a given template and starting index.
 */
const buildSlideItems = (items, idx, template) => {
  const item = items[idx];

  switch (template) {
    case "1up":
      return [item];
    case "2next":
      return items.slice(idx, Math.min(idx + 2, items.length));
    case "4next":
      return items.slice(idx, Math.min(idx + 4, items.length));
    case "n2Up":
    case "n2Up-mirror-left":
    case "n2Up-mirror-right":
      return [item, item];
    case "4up":
    case "4up-warhol":
      return [item, item, item, item];
    case "grid-5x4-center":
      return [item, ...Array(14).fill(item)];
    default:
      return [item];
  }
};

/**
 * Map a raw template name to the CSS layout class token.
 */
const getCssTemplate = (template) => {
  if (
    template === "2next" ||
    template === "n2Up" ||
    template === "n2Up-mirror-left" ||
    template === "n2Up-mirror-right"
  ) {
    return "n2Up";
  }
  if (
    template === "4next" ||
    template === "4up" ||
    template === "4up-warhol"
  ) {
    return "4up";
  }
  if (template === "grid-5x4-center") {
    return "grid-5x4-center";
  }
  return "1up";
};

/**
 * Whether this template uses multiple items per slide.
 */
const isMultiItemTemplate = (cssTemplate) => cssTemplate !== "1up";

/**
 * Determine orientation class from effective aspect ratio.
 */
const getOrientationClass = (aspectRatio) => {
  if (aspectRatio === null) return "";
  if (aspectRatio > 1.1) return "carousel__item--landscape";
  if (aspectRatio < 0.9) return "carousel__item--portrait";
  return "carousel__item--square";
};

/**
 * Compute the effective aspect ratio of an image accounting for rotation.
 */
const computeEffectiveAspect = (width, height, rotation) => {
  const rot = rotation ? parseInt(rotation) : 0;
  const isRotated90or270 =
    Math.abs(rot) === 90 || Math.abs(rot) === 270;

  const effectiveWidth = isRotated90or270 ? height || width : width || height;
  const effectiveHeight = isRotated90or270 ? width || height : height || width;

  return effectiveWidth && effectiveHeight
    ? effectiveWidth / effectiveHeight
    : null;
};

/**
 * Common handler for both <img onLoad> and <video onLoadedMetadata>.
 * Updates CSS custom properties and orientation class on the wrapper.
 */
const handleMediaLoad = (mediaEl, image, orientationClass) => {
  const isVideo = mediaEl.tagName === "VIDEO";
  const rawWidth = image?.width || (isVideo ? mediaEl.videoWidth : mediaEl.naturalWidth);
  const rawHeight = image?.height || (isVideo ? mediaEl.videoHeight : mediaEl.naturalHeight);
  const rotation = image?.rotation ? parseInt(image.rotation) : 0;
  const isRotated90or270 =
    Math.abs(rotation) === 90 || Math.abs(rotation) === 270;

  const effectiveWidth = isRotated90or270 ? rawHeight : rawWidth;
  const effectiveHeight = isRotated90or270 ? rawWidth : rawHeight;
  const finalAspectRatio = effectiveWidth / effectiveHeight;

  const wrapper = mediaEl.closest(".carousel__item-wrapper");
  if (wrapper) {
    wrapper.style.setProperty("--item-width", `${rawWidth}px`);
    wrapper.style.setProperty("--item-height", `${rawHeight}px`);
    wrapper.style.setProperty(
      "--item-aspect-ratio",
      finalAspectRatio.toString()
    );
  }

  if (!orientationClass) {
    if (finalAspectRatio > 1.1) {
      mediaEl.classList.add("carousel__item--landscape");
    } else if (finalAspectRatio < 0.9) {
      mediaEl.classList.add("carousel__item--portrait");
    } else {
      mediaEl.classList.add("carousel__item--square");
    }
  }
};

/**
 * Compute per-item slot / style / tint / mirror / filter metadata.
 */
const getItemMeta = (
  cssTemplate,
  template,
  slideItems,
  itemIdx,
  image
) => {
  let slotPosition = "";
  let warholTint = "";
  let filterClass = "";
  let mirrorClass = "";
  let gridStyle = {};

  if (cssTemplate === "n2Up" && slideItems.length === 2) {
    slotPosition =
      itemIdx === 0 ? "carousel__slot--left" : "carousel__slot--right";
    if (template === "n2Up-mirror-left" && itemIdx === 0) {
      mirrorClass = "carousel__mirror--flip";
    } else if (template === "n2Up-mirror-right" && itemIdx === 1) {
      mirrorClass = "carousel__mirror--flip";
    }
  } else if (cssTemplate === "4up" && slideItems.length === 4) {
    slotPosition =
      itemIdx === 0 || itemIdx === 2
        ? "carousel__slot--left"
        : "carousel__slot--right";
  } else if (cssTemplate === "grid-5x4-center" && slideItems.length === 15) {
    if (itemIdx === 0) {
      slotPosition = "carousel__slot--center";
      gridStyle = {
        gridColumn: "2 / 5",
        gridRow: "2 / 4",
      };
    } else {
      slotPosition = "carousel__slot--border";
      const pos = BORDER_POSITIONS[itemIdx];
      if (pos) {
        gridStyle = {
          gridColumn: pos.col,
          gridRow: pos.row,
        };
      }
    }
  }

  // Warhol tints override filters
  if (template === "4up-warhol" && slideItems.length === 4) {
    warholTint = `carousel__warhol--${WARHOL_TINTS[itemIdx]}`;
  } else if (image?.filter) {
    filterClass = `carousel__filter--${image.filter}`;
  }

  return { slotPosition, warholTint, filterClass, mirrorClass, gridStyle };
};

/**
 * Render a single media item (img or video) inside its wrapper div.
 *
 * For n2Up templates with 90/270-degree rotation, wraps the media element
 * in a rotation container that has the effective (post-rotation) dimensions.
 * CSS transform: rotate() doesn't affect layout, so without the wrapper a
 * rotated landscape image would keep its wide layout box and prevent the
 * flex alignment from pushing items toward the center line.
 */
const renderMediaItem = (image, itemIdx, cssTemplate, template, slideItems, isDuplicated) => {
  const isVideo = image?.mimeType && image.mimeType.startsWith("video/");
  const uniqueKey = isDuplicated ? `${image?.id}-${itemIdx}` : image?.id;

  const width = image?.width;
  const height = image?.height;
  const rotation = image?.rotation ? parseInt(image.rotation) : 0;
  const aspectRatio = computeEffectiveAspect(width, height, image?.rotation);
  const orientationClass = getOrientationClass(aspectRatio);

  const {
    slotPosition,
    warholTint,
    filterClass,
    mirrorClass,
    gridStyle,
  } = getItemMeta(cssTemplate, template, slideItems, itemIdx, image);

  const styleProps = {
    ...(width && height
      ? {
          "--item-width": `${width}px`,
          "--item-height": `${height}px`,
          "--item-aspect-ratio": aspectRatio,
        }
      : {}),
    ...gridStyle,
  };

  // Detect if this item needs a rotation wrapper for correct layout.
  // CSS transform: rotate() doesn't change layout dimensions, so without a wrapper:
  // - n2Up: rotated landscape images keep wide layout boxes, preventing center alignment
  // - 1up: rotated portrait images keep narrow layout boxes, not filling screen as landscape
  const isRotated90or270 = Math.abs(rotation) === 90 || Math.abs(rotation) === 270;
  const needsRotateWrap = isRotated90or270 && aspectRatio !== null && (
    // n2Up: only when effective result is portrait (ratio < 1) to fix flex alignment
    (cssTemplate === "n2Up" && aspectRatio < 1) ||
    // 1up: always wrap rotated items so they display with effective dimensions
    cssTemplate === "1up"
  );

  // For non-wrapped items, apply rotation directly via inline style.
  // For wrapped items, rotation is handled by the container.
  // Mirror flip is handled on the wrapper div (see wrapperTransform above).
  const itemStyle = (!needsRotateWrap && rotation) ? { transform: `rotate(${rotation}deg)` } : {};

  // Build rotation container style based on template type.
  let rotateContainerStyle = null;
  if (needsRotateWrap && cssTemplate === "1up") {
    // 1up rotation: the container is sized to the original (pre-rotation) aspect ratio
    // and then the entire container is rotated. This makes the visual result match
    // the effective (post-rotation) dimensions while fitting within the viewport.
    //
    // The min() functions implement "contain" behavior:
    // - width: fill viewport height, but don't exceed viewport width / effectiveAR
    // - height: fill viewport width, but don't exceed viewport height * effectiveAR
    // After rotation, the visual dimensions fit within the viewport.
    rotateContainerStyle = {
      position: "relative",
      overflow: "hidden",
      width: `min(100vh, calc(100vw / ${aspectRatio.toFixed(4)}))`,
      height: `min(100vw, calc(100vh * ${aspectRatio.toFixed(4)}))`,
      flexShrink: 0,
      transform: `rotate(${rotation}deg)`,
      transformOrigin: "center",
    };
  } else if (needsRotateWrap) {
    // n2Up rotation: prioritize filling the viewport height.
    // The image is absolutely positioned and rotated 90° inside the container.
    // After rotation, the image's original width becomes the visual height, and
    // the original height becomes the visual width.
    //
    // To fill viewport height: the image's pre-rotation width must = 100vh.
    // To fit in the 50% slot: the image's pre-rotation height must ≤ 50vw.
    // We use viewport units (vh/vw) instead of percentages so sizing is
    // independent of the container dimensions.
    // For a landscape image (AR > 1) rotated 90°:
    //   pre-rotation width (→ visual height) = 100vh → fills viewport height
    //   pre-rotation height (→ visual width) = 100vh / AR → fits in the slot
    // For a portrait image (AR < 1) rotated 90°:
    //   same formula, but visual width = 100vh / AR > 100vh, so we clamp to 50vw
    const preRotW = "100vh";
    const preRotH = `min(50vw, ${(100 / aspectRatio).toFixed(2)}vh)`;
    rotateContainerStyle = {
      position: "relative",
      height: "100vh",
      width: "50vw",
      flexShrink: 0,
      overflow: "hidden",
      "--rotate-w": preRotW,
      "--rotate-h": preRotH,
      "--rotate-transform": `translate(-50%, -50%) rotate(${rotation}deg)${mirrorClass ? " scaleX(-1)" : ""}`,
    };
  }

  const mediaElement = isVideo ? (
    <video
      className={`carousel__item ${orientationClass}`}
      style={itemStyle}
      src={image?.url}
      onClick={(e) => e.stopPropagation()}
      controls
      autoPlay
      muted
      playsInline
      loop
      onLoadedMetadata={(e) =>
        handleMediaLoad(e.target, image, orientationClass)
      }
    />
  ) : (
    <img
      className={`carousel__item ${orientationClass}`}
      style={itemStyle}
      src={image?.url}
      alt={image?.name || "carousel item"}
      onClick={(e) => e.stopPropagation()}
      onLoad={(e) =>
        handleMediaLoad(e.target, image, orientationClass)
      }
    />
  );

  // Wrap media in a mirror container when needed. This sits between the
  // layout wrapper (which handles flex alignment) and the img/video, so
  // flex positioning stays correct while the visual content is flipped.
  // Using a wrapper instead of inline style on img/video avoids conflicts
  // with CSS animations (fragment, turbulentDissolve) that animate transform.
  let content;
  if (rotateContainerStyle) {
    content = (
      <div
        className={`carousel__rotate-container${cssTemplate === "1up" ? " carousel__rotate-container--1up" : ""}`}
        style={rotateContainerStyle}
      >
        {mediaElement}
      </div>
    );
  } else if (mirrorClass) {
    content = (
      <div className="carousel__mirror-container" style={{ transform: "scaleX(-1)", height: "100%" }}>
        {mediaElement}
      </div>
    );
  } else {
    content = mediaElement;
  }

  return (
    <div
      key={uniqueKey}
      className={`carousel__item-wrapper carousel__item-wrapper--${cssTemplate} ${slotPosition} ${warholTint} ${filterClass} ${mirrorClass}`.trim()}
      style={styleProps}
    >
      {content}
    </div>
  );
};

/**
 * SlideRenderer builds the array of <div> slides consumed by <Carousel>.
 *
 * It is a pure rendering function -- no hooks, no side effects. It receives
 * all the data it needs via props.
 *
 * For multi-item templates (n2Up, 4up, grid-5x4-center), a single
 * AnimationHandler wraps all items so they transition together. For 1up,
 * each item gets its own AnimationHandler.
 */
export const SlideRenderer = ({
  state,
  slideIndex,
  catalogData,
  folderItem,
  isPublic,
}) => {
  const slides = [];
  let idx = 0;
  let slideIdx = 0;
  const items = state;

  while (idx < items.length) {
    const item = items[idx];
    const template = item?.template || "1up";
    const advanceBy = getAdvanceBy(template);
    const slideItems = buildSlideItems(items, idx, template);
    const isActive = slideIdx === slideIndex;
    const cssTemplate = getCssTemplate(template);

    // Get transition type with fallback to collection default
    const defaultTransitionType = isPublic
      ? catalogData?.transitionType || "fadeInOut"
      : folderItem?.transitionType || "fadeInOut";

    const transitionType = item?.transitionType || defaultTransitionType;

    const isDuplicated =
      template === "n2Up" ||
      template === "n2Up-mirror-left" ||
      template === "n2Up-mirror-right" ||
      template === "4up" ||
      template === "4up-warhol" ||
      template === "grid-5x4-center";

    if (isMultiItemTemplate(cssTemplate)) {
      // Multi-item template: ONE AnimationHandler wraps ALL items.
      // The inner container provides the flex/grid layout.
      slides.push(
        <div
          key={`slide-${slideIdx}`}
          className={`carousel__slide carousel__slide--${cssTemplate}`}
        >
          <AnimationHandler
            isActive={isActive}
            rotation={0}
            type={transitionType}
            transitionDuration={item?.timePerSlide}
          >
            <div className={`carousel__slide-inner carousel__slide-inner--${cssTemplate}`}>
              {slideItems.map((image, itemIdx) =>
                renderMediaItem(image, itemIdx, cssTemplate, template, slideItems, isDuplicated)
              )}
            </div>
          </AnimationHandler>
        </div>
      );
    } else {
      // 1up template: AnimationHandler wraps the single item directly.
      const image = slideItems[0];
      // For 90/270° rotation, the rotation is handled by the rotation container
      // inside renderMediaItem, not by AnimationHandler. This allows proper
      // dimension-aware rotation that stretches the item to fill the screen.
      const imageRotation = image?.rotation ? parseInt(image.rotation) : 0;
      const use1upRotateWrap = Math.abs(imageRotation) === 90 || Math.abs(imageRotation) === 270;
      slides.push(
        <div
          key={`slide-${slideIdx}`}
          className={`carousel__slide carousel__slide--${cssTemplate}`}
        >
          <AnimationHandler
            isActive={isActive}
            rotation={use1upRotateWrap ? 0 : image?.rotation}
            type={transitionType}
            transitionDuration={image?.timePerSlide}
          >
            {renderMediaItem(image, 0, cssTemplate, template, slideItems, false)}
          </AnimationHandler>
        </div>
      );
    }

    idx += advanceBy;
    slideIdx++;
  }

  return slides;
};
